import React from "react";
import { Modal, Button } from "common/components";
import { Warning, Info, Error, CheckCircle } from "@mui/icons-material";
import "./ConfirmationModal.scss";

export type ConfirmationVariant = "warning" | "danger" | "info" | "success";

interface ConfirmationModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  variant?: ConfirmationVariant;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  title,
  message,
  variant = "warning",
  confirmText = "Confirm",
  cancelText = "Cancel",
  onConfirm,
  onCancel,
  isLoading = false
}) => {
  const getIcon = () => {
    switch (variant) {
      case "danger":
        return <Error className="confirmation-icon danger" />;
      case "warning":
        return <Warning className="confirmation-icon warning" />;
      case "info":
        return <Info className="confirmation-icon info" />;
      case "success":
        return <CheckCircle className="confirmation-icon success" />;
      default:
        return <Warning className="confirmation-icon warning" />;
    }
  };

  const getConfirmButtonVariant = () => {
    switch (variant) {
      case "danger":
        return "warn";
      case "warning":
        return "warn";
      case "info":
        return "primary";
      case "success":
        return "primary";
      default:
        return "warn";
    }
  };

  const footerElement = (
    <div className="confirmation-modal-actions">
      <Button
        variant="secondary"
        onClick={onCancel}
        disabled={isLoading}
        className="cancel-button"
      >
        {cancelText}
      </Button>
      <Button
        variant={getConfirmButtonVariant()}
        onClick={onConfirm}
        disabled={isLoading}
        className="confirm-button"
      >
        {isLoading ? "Processing..." : confirmText}
      </Button>
    </div>
  );

  return (
    <Modal
      show={isOpen}
      title={title}
      size="sm"
      className="confirmation-modal"
      headerClassName="confirmation-modal-header"
      bodyClassName="confirmation-modal-body"
      footerClassName="confirmation-modal-footer"
      footerElement={footerElement}
      onClose={onCancel}
    >
      <div className="confirmation-content">
        <div className="confirmation-icon-container">{getIcon()}</div>
        <div className="confirmation-message">{message}</div>
      </div>
    </Modal>
  );
};

export default ConfirmationModal;
